import react, { useEffect, useRef, useState } from 'react';

import style from './EditNotificationDeviceLay.module.css';
import { NotificationDevice } from '../../../Models/Devices/NotificationDevice';
import { Button, Divider, Popconfirm, Tour } from 'antd';

import {
  ArrowLeftOutlined,
  TableOutlined,
  PartitionOutlined,
  PlusSquareOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type { GetRef, TourProps } from 'antd';

import { useTranslation } from 'react-i18next';
import { EditNotificationGlobal } from '../Forms/EditNotificationGlobal/EditNotificationGlobal';
import { serviceBundler, webSocketService } from '../../../App';

import { WsDeviceUpdate } from '../../../Models/WebSocket/Outgoing/WsDeviceUpdate';
import { DbConnectedSensorBoolean } from '../../../Models/Devices/DbConnectedSensorBoolean';
import { EditNotificationSensorBoolean } from '../Forms/EditNotificationSensorBoolean/EditNotificationSensorBoolean';
import { DbConnectedSensorNumber } from '../../../Models/Devices/DbConnectedSensorNumber';
import { EditNotificationSensorNumber } from '../Forms/EditNotificationSensorNumber/EditNotificationSensorNumber';

import { v4 as uuidv4 } from 'uuid';
import { Transput } from '../../../Models/Platform/Transput';
import { AddNotification } from '../Forms/AddNotification/AddNotification';
import { ResultAddNotification } from '../../../Enums/ResultAddNotification';
import { a } from '@react-spring/web';
import { TourHelper } from '../../../Helpers/TourHelper';

const EditNotificationDeviceLay = (props: {
  allData: NotificationDevice[];
  data?: NotificationDevice;
  dataTransputs: Transput[];
  onRemove: Function;
  onSafe: Function;
  onSafeWithoutClose: Function;
  onCancel: Function;
}) => {
  const { t, i18n } = useTranslation();

  const [currentDevice, setCurrentDevice] = useState<NotificationDevice>();
  const [showGeneral, setShowGeneral] = useState<boolean>(true);
  const [modalNotificationOpen, setModalNotificationOpen] = useState(false);

  const [currentSensorBool, setCurrentSensorBool] =
    useState<DbConnectedSensorBoolean | null>();
  const [currentSensorNumber, setCurrentSensorNumber] =
    useState<DbConnectedSensorNumber | null>();

  const [safeDevice, setSafeDevice] = useState<boolean>(false);
  const [safeDeviceAndClose, setSafeDeviceAndClose] = useState<boolean>(false);
  const [validationGlobal, setValidationGlobal] = useState<boolean>(false);
  const [validationSensor, setValidationSensor] = useState<boolean>(false);

  const [currentTourStep, setCurrentTourStep] = useState<number>(0);

  const refStep1 = useRef<any>(null);
  const refStep2 = useRef<any>(null);
  const refStep3 = useRef<any>(null);

  const globalRef = useRef<any>(null);
  const sensorBoolRef = useRef<any>(null);
  const sensorNumberRef = useRef<any>(null);

  useEffect(() => {
    if (props.data == null) {
      return;
    }

    setCurrentDevice(props.data);
  }, [props.data]);

  useEffect(() => {
    if (
      !validationSensor &&
      currentSensorBool != null &&
      currentSensorNumber != null
    ) {
      return;
    }

    if (safeDevice) {
      SafeDeviceAfterUpdate();
    }
  }, [validationGlobal, validationSensor, safeDevice, safeDeviceAndClose]);

  useEffect(() => {
    serviceBundler.tourService.TourDevicesPageSubject.subscribe((value) => {
      if (value) {
        if (serviceBundler.tourService.tourDevicesPageActive) {
          setCurrentTourStep(1);
        }
      }
    });
  }, [0]);

  const steps: TourProps['steps'] = [
    TourHelper.CreateTourSteps(
      t,
      'Algemeen',
      'Hier kun je de algemene instellingen van het apparaat aanpassen. \n Hier kun je de naam, beschrijving en andere instellingen wijzigen.',
      'left',
      refStep1.current,
    ),
    TourHelper.CreateTourSteps(
      t,
      'Sensoren',
      'Door hier op te klikken kun je de sensoren van het apparaat beheren',
      'left',
      refStep2.current,
    ),
  ];

  const CancelEdit = () => {
    props.onCancel(currentDevice);
  };

  const ShowGeneralData = () => {
    SafeOldSensorBoolData();

    setCurrentSensorBool(null);
    setCurrentSensorNumber(null);
    setShowGeneral(true);
  };

  const ShowSpecificSensorDataBool = (item: DbConnectedSensorBoolean) => {
    SafeOldGlobalData();
    SafeOldSensorBoolData();

    setShowGeneral(false);
    setCurrentSensorNumber(null);
    setCurrentSensorBool(item);
  };

  const ShowSpecificSensorDataNumber = (item: DbConnectedSensorNumber) => {
    SafeOldGlobalData();
    SafeOldSensorNumberData();

    setShowGeneral(false);
    setCurrentSensorBool(null);
    setCurrentSensorNumber(item);
  };

  const SafeOldGlobalData = () => {
    if (currentDevice) {
      const node = globalRef.current;
      if (node) {
        node.SafeGlobal();
      }
    }
  };

  const SafeOldSensorBoolData = () => {
    if (currentSensorBool) {
      const node = sensorBoolRef.current;
      if (node) {
        node.SafeSensor();
      }
    }
  };

  const SafeOldSensorNumberData = () => {
    if (currentSensorNumber) {
      const node = sensorNumberRef.current;
      if (node) {
        node.SafeSensor();
      }
    }
  };

  const SafeGlobalCallBack = (item: NotificationDevice) => {
    setCurrentDevice(item);
    setShowGeneral(false);
    setValidationGlobal(true);
    setSafeDevice(true);
  };

  const SafeSensorBoolCallBack = (item: DbConnectedSensorBoolean) => {
    if (item == null) {
      setValidationSensor(false);
      return;
    }
    if (currentDevice == undefined) {
      return;
    }

    var indexSensor = currentDevice.connectedSensorsBoolean.findIndex(
      (x) => x.id == item.id,
    );

    if (indexSensor >= 0) {
      currentDevice.connectedSensorsBoolean[indexSensor] = item;
    } else {
      currentDevice.connectedSensorsBoolean.push(item);
    }

    setCurrentDevice(currentDevice);
    setValidationSensor(true);
    setSafeDevice(true);
  };

  const SafeSensorNumberCallBack = (item: DbConnectedSensorNumber) => {
    if (item == null) {
      setValidationSensor(false);
      return;
    }
    if (currentDevice == undefined) {
      return;
    }

    var indexSensor = currentDevice.connectedSensorsNumber.findIndex(
      (x) => x.id == item.id,
    );

    if (indexSensor >= 0) {
      currentDevice.connectedSensorsNumber[indexSensor] = item;
    } else {
      currentDevice.connectedSensorsNumber.push(item);
    }

    setCurrentDevice(currentDevice);
    setValidationSensor(true);
    setSafeDevice(true);
  };

  const RemoveSensorBoolean = async (item: DbConnectedSensorBoolean) => {
    if (!currentDevice) {
      return;
    }

    var indexSensor = currentDevice?.connectedSensorsBoolean.findIndex(
      (x) => x.id == item.id,
    );

    if (indexSensor >= 0) {
      currentDevice.connectedSensorsBoolean.splice(indexSensor, 1);
      setCurrentDevice(currentDevice);
      setCurrentSensorBool(null);
      setShowGeneral(true);
    }

    var device =
      await serviceBundler.devicesService.UpdateAndSafeNotificationDevice(
        currentDevice,
      );
    props.onSafeWithoutClose(currentDevice);
  };

  const RemoveSensorNumber = async (item: DbConnectedSensorNumber) => {
    if (!currentDevice) {
      return;
    }

    var indexSensor = currentDevice?.connectedSensorsNumber.findIndex(
      (x) => x.id == item.id,
    );

    if (indexSensor >= 0) {
      currentDevice.connectedSensorsNumber.splice(indexSensor, 1);
      setCurrentDevice(currentDevice);
      setCurrentSensorNumber(null);
      setShowGeneral(true);
    }

    var device =
      await serviceBundler.devicesService.UpdateAndSafeNotificationDevice(
        currentDevice,
      );
    props.onSafeWithoutClose(currentDevice);
  };

  const AddSensorForState = () => {
    SafeOldGlobalData();
    if (currentDevice) {
      var newSensor = new DbConnectedSensorBoolean();
      newSensor.id = uuidv4();
      setCurrentSensorBool(newSensor);
      setCurrentSensorNumber(null);
      setShowGeneral(false);
    }
  };

  const AddSensorForNumberic = () => {
    SafeOldGlobalData();
    if (currentDevice) {
      var newSensor = new DbConnectedSensorNumber();
      newSensor.id = uuidv4();
      setCurrentSensorNumber(newSensor);
      setCurrentSensorBool(null);
      setShowGeneral(false);
    }
  };

  const AddSensorForString = () => {
    SafeOldGlobalData();
    if (currentDevice) {
      var newSensor = new DbConnectedSensorNumber();
      newSensor.id = uuidv4();
      setCurrentSensorNumber(newSensor);
      setCurrentSensorBool(null);
      setShowGeneral(false);
    }
  };

  const RemoveDevice = async () => {
    if (currentDevice) {
      serviceBundler.devicesService.RemoveNotificationDevice(currentDevice);
      props.onRemove(currentDevice);
    }
  };

  const SafeDeviceBtn = async () => {
    SafeOldSensorBoolData();
    SafeOldSensorNumberData();
    SafeOldGlobalData();
    setSafeDeviceAndClose(true);
  };

  const SafeDeviceAfterUpdate = async () => {
    if (currentDevice) {
      setSafeDevice(false);
      setValidationGlobal(false);
      setValidationSensor(false);

      if (safeDeviceAndClose) {
        var device =
          await serviceBundler.devicesService.UpdateAndSafeNotificationDevice(
            currentDevice,
          );

        setCurrentDevice(device);

        webSocketService.sendMessage(new WsDeviceUpdate());
        props.onSafe(currentDevice);
      }
    }
  };

  const CancelOperation = () => {
    if (currentDevice) {
      if (currentDevice.id == '') {
        serviceBundler.devicesService.RemoveNotificationDevice(currentDevice);
      }
    }

    props.onCancel(currentDevice);
  };

  const ChooseNotificationType = (result: ResultAddNotification) => {
    if (result === ResultAddNotification.AddSensorBoolean) {
      AddSensorForState();
    } else if (result === ResultAddNotification.AddSensorNumber) {
      AddSensorForNumberic();
    } else if (result === ResultAddNotification.AddSensorString) {
      AddSensorForString();
    }
    setModalNotificationOpen(false);
  };

  const StartSpecicifcTour = (step: number) => {
    if (currentTourStep == step) {
      return true;
    }
    return false;
  };

  const SetNumberOfTourStep = (index: number) => {
    setCurrentTourStep(index);
  };

  const TourReady = () => {
    setCurrentTourStep((tourSteps) => tourSteps + 1);
    serviceBundler.tourService.EndDevicesPageTour();
  };

  return (
    <>
      <Tour
        open={StartSpecicifcTour(1)}
        onClose={() => TourReady()}
        steps={steps}
      />
      <AddNotification
        open={modalNotificationOpen}
        onCancel={() => setModalNotificationOpen(false)}
        onChoose={ChooseNotificationType}
      ></AddNotification>
      <div className={style.bodyLay}>
        <div className={style.bodyTextBadge}>
          <Button icon={<ArrowLeftOutlined />} onClick={CancelEdit}></Button>
        </div>
        <div className={style.paneEdit}>
          <div className={style.paneRow}>
            <div className={style.itemsModule}>
              <Divider>{t('sensors')}</Divider>
              <div ref={refStep1}>
                <Button
                  onClick={() => ShowGeneralData()}
                  icon={<TableOutlined />}
                  style={{
                    textAlign: 'start',
                    height: '50px',
                    borderColor: showGeneral ? '#C1D202' : '',
                    color: showGeneral ? '#C1D202' : '',
                  }}
                  type="default"
                  block
                >
                  {t('general')}
                </Button>
              </div>

              <div className={style.groupSensors} ref={refStep2}>
                {currentDevice?.connectedSensorsBoolean.map(
                  (item: DbConnectedSensorBoolean, i) => (
                    <Button
                      icon={<PartitionOutlined />}
                      style={{
                        borderColor:
                          item.id == currentSensorBool?.id ? '#C1D202' : '',
                        color:
                          item.id == currentSensorBool?.id ? '#C1D202' : '',
                        marginLeft: 0,
                      }}
                      className={style.btnSensor}
                      onClick={() => ShowSpecificSensorDataBool(item)}
                    >
                      {item.sensorName}
                    </Button>
                  ),
                )}
                {currentDevice?.connectedSensorsNumber.map(
                  (item: DbConnectedSensorNumber, i) => (
                    <Button
                      icon={<PartitionOutlined />}
                      style={{
                        borderColor:
                          item.id == currentSensorNumber?.id ? '#C1D202' : '',
                        color:
                          item.id == currentSensorNumber?.id ? '#C1D202' : '',
                        marginLeft: 0,
                      }}
                      className={style.btnSensor}
                      onClick={() => ShowSpecificSensorDataNumber(item)}
                    >
                      {item.sensorName}
                    </Button>
                  ),
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'end',
                  marginRight: 3,
                  marginTop: 10,
                  gap: 10,
                }}
              >
                <Button
                  style={{ width: 300 }}
                  onClick={() => setModalNotificationOpen(true)}
                  icon={<PlusSquareOutlined />}
                >
                  {t('addNotification')}
                </Button>
                {/* <Button
                  style={{ width: 300 }}
                  onClick={AddSensorForNumberic}
                  icon={<PlusSquareOutlined />}
                >
                  {t('addSensorForNumber')}
                </Button> */}
              </div>
            </div>
            <div className={style.propsModule}>
              {showGeneral && (
                <EditNotificationGlobal
                  ref={globalRef}
                  allData={props.allData}
                  data={currentDevice}
                  onCancel={() => 1 == 1}
                  onSafe={SafeGlobalCallBack}
                ></EditNotificationGlobal>
              )}
              {currentSensorBool != null && (
                <EditNotificationSensorBoolean
                  ref={sensorBoolRef}
                  allData={currentDevice?.connectedSensorsBoolean}
                  data={currentSensorBool}
                  dataTransputs={props.dataTransputs}
                  dataDevice={currentDevice}
                  onCancel={() => 1 == 1}
                  onRemove={RemoveSensorBoolean}
                  onSafe={SafeSensorBoolCallBack}
                ></EditNotificationSensorBoolean>
              )}
              {currentSensorNumber != null && (
                <EditNotificationSensorNumber
                  ref={sensorNumberRef}
                  allData={currentDevice?.connectedSensorsNumber}
                  data={currentSensorNumber}
                  dataDevice={currentDevice}
                  dataTransputs={props.dataTransputs}
                  onCancel={() => 1 == 1}
                  onRemove={RemoveSensorNumber}
                  onSafe={SafeSensorNumberCallBack}
                ></EditNotificationSensorNumber>
              )}
            </div>
          </div>
          <div className={style.btnGroup}>
            <Button
              onClick={CancelOperation}
              type="primary"
              icon={<CloseOutlined />}
            >
              {t('cancel')}
            </Button>
            {currentDevice?.id !== undefined && (
              <Popconfirm
                title={t('removeDevice')}
                description={t('areYouSure')}
                onConfirm={RemoveDevice}
                okText={t('yes')}
                cancelText={t('no')}
              >
                <Button type="primary" icon={<DeleteOutlined />}>
                  {t('remove')}
                </Button>
              </Popconfirm>
            )}
            <Button
              onClick={SafeDeviceBtn}
              type="primary"
              icon={<SaveOutlined />}
            >
              {t('safe')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditNotificationDeviceLay;
